import { getAuthResponse, collectBankId } from '../../Api/ApiUser';
import { checkHintCode, removeCache } from '@/helpers';

const initialState = () => ({
    bankID: null,
    checked: false,
});

const state = initialState();

const mutations = {
    SET_BANK_ID(state, obj) {
        state.bankID = {
            orderRef: obj.orderRef,
            autoStartToken: obj.autoStartToken,
            qrStartToken: obj.qrStartToken,
            qrStartSecret: obj.qrStartSecret,
            status: null,
            hintCode: '',
            time: 1,
            animation: obj.animation,
        };
    },
    SET_TIME(state) {
        if (state.bankID) {
            state.bankID.time = state.bankID.time + 1;
        }
    },
    SET_ANIMATION(state, str) {
        state.bankID.animation = str;
    },
    SET_HINT_AND_STATUS_CODE(state, obj) {
        if (!state?.bankID) {
            return;
        }

        state.bankID.status = obj.status;
        state.bankID.hintCode = obj.hintCode;
    },
    RESET_BANK_ID(state) {
        state.bankID = null;
    },
    CHECKED_IF_USER_EXISTS(state) {
        state.checked = true;
    },
    RESET_STATE: (state) => Object.assign(state, initialState()),
};

const actions = {
    async BANK_ID_PREPARE({ commit }) {
        await getAuthResponse().then((response) => {
            if (response.status === 'Success') {
                commit('SET_BANK_ID', response.data);
            }
        });
    },
    async BANK_ID_POLLING({ commit, state, rootState, dispatch }, payload) {
        if (!state.bankID) {
            return;
        }

        const data = { time: state.bankID?.time ?? 1 };

        if (state.bankID.qrStartToken) {
            data.qrStartToken = state.bankID.qrStartToken;
        }

        if (state.bankID.qrStartSecret) {
            data.qrStartSecret = state.bankID.qrStartSecret;
        }

        await collectBankId(state.bankID.orderRef, data).then((response) => {
            if (response.data && 'hintCode' in response.data) {
                let intent;

                switch (payload.route) {
                    case 'login':
                        intent = 'login';
                        break;
                    case 'educate-register':
                    case 'employ-register':
                    case 'accept-department-invite':
                        intent = 'register';
                        break;
                    default:
                        intent = 'default';
                        break;
                }

                const checker = checkHintCode(response.data.hintCode, intent);

                if (response.data.animation) {
                    commit('SET_ANIMATION', response.data.animation);
                }

                if (checker.status) {
                    if (payload.route === 'login') {
                        commit('UserStore/SET_TOKEN', response.data.token, { root: true });
                        dispatch('Departments/SET_DEPARTMENTS', response.data.departments, { root: true });
                        dispatch('UserStore/GET_ME', null, { root: true });
                    }

                    if (payload.route === 'educate-register') {
                        dispatch('UserStore/GET_ME', null, { root: true }).then(() => {
                            commit('RegisterStore/SET_UPDATE_STEP_VIEW', 'PROFILE_PICKER', { root: true });
                            commit('RegisterStore/SET_HAS_ACCOUNT', true, { root: true });
                        });
                    }
                    if (payload.route === 'employ-register') {
                        dispatch('UserStore/GET_ME', null, { root: true }).then(() => {
                            commit('RegisterStore/SET_UPDATE_STEP_VIEW', 'PICKER', { root: true });
                            commit('RegisterStore/SET_HAS_ACCOUNT', true, { root: true });
                        });
                    }
                }

                commit('SET_HINT_AND_STATUS_CODE', {
                    status: checker.status,
                    hintCode: checker.hintCode,
                });

                if (!checker.status) {
                    if (checker.snackbar) {
                        commit('GlobalStore/SET_SNACKBAR', checker.snackbar, { root: true });
                    }

                    if (payload.route === 'login') {
                        if (checker.hintCode === 'cervdNoUser') {
                            commit('RESET_BANK_ID');
                        }
                    }

                    if (payload.route === 'educate-register') {
                        if (checker.error) {
                            commit('RESET_BANK_ID');
                            commit('RegisterStore/RESET_STATE', null, { root: true });
                        }

                        if (checker.hintCode === 'cervdNoUser') {
                            commit(
                                'RegisterStore/SET_USER',
                                {
                                    first_name: response.data.user.first_name,
                                    last_name: response.data.user.last_name,
                                    national_identity_number: response.data.user.national_identity_number,
                                    email: null,
                                    departments: [],
                                    incomplete_user: null,
                                },
                                { root: true }
                            );

                            commit('RegisterStore/SET_UPDATE_STEP', 1, { root: true });
                            commit('RegisterStore/SET_UPDATE_STEP_VIEW', 'NEW', { root: true });
                        }
                    }

                    if (payload.route === 'employ-register') {
                        if (checker.error) {
                            commit('RESET_BANK_ID');
                            commit('RegisterStore/RESET_STATE', null, { root: true });
                        }

                        if (checker.hintCode === 'cervdNoUser') {
                            commit(
                                'RegisterStore/SET_USER',
                                {
                                    first_name: response.data.user.first_name,
                                    last_name: response.data.user.last_name,
                                    national_identity_number: response.data.user.national_identity_number,
                                    email: null,
                                    departments: [],
                                    incomplete_user: null,
                                },
                                { root: true }
                            );

                            commit('RegisterStore/SET_UPDATE_STEP_VIEW', 'NEW', { root: true });
                        }
                    }

                    if (payload.route === 'accept-department-invite') {
                        if (checker.hintCode === 'cervdNoUser') {
                            commit('GlobalStore/SET_LOADING', true, { root: true });

                            // If the user is not registered, claim the invite and register the user
                            commit(
                                'RegisterStore/SET_USER',
                                {
                                    first_name: response.data.user.first_name,
                                    last_name: response.data.user.last_name,
                                    national_identity_number: response.data.user.national_identity_number,
                                    email:
                                        rootState.RegisterStore.register.email ||
                                        rootState.UserStore.user.incomplete_user.email,
                                },
                                { root: true }
                            );

                            dispatch('RegisterStore/REGISTER_USER', null, { root: true });
                        } else if (
                            checker.hintCode === 'cervdUserAlreadyExists' &&
                            rootState.UserStore.user.incomplete_user.incomplete
                        ) {
                            // The national identity number is already registered, but as the invited user is complete
                            // Assume that the user is trying to claim the invite, and merge the accounts
                            dispatch(
                                'UserStore/MERGE_USERS',
                                {
                                    hash: response.data.user.hash,
                                },
                                { root: true }
                            );
                        }

                        return;
                    }
                }

                if (checker.status !== null) {
                    if (payload.route === 'single-certificate') {
                        dispatch('SingleCertificate/GET_CERTIFICATED_USER', {}, { root: true });
                    }
                }

                if (checker.error && checker.hintCode === 'cervdUserAlreadyExists') {
                    removeCache();
                }
            }
        });

        // Update query param t for BankID QR code animation
        commit('SET_TIME');
    },
};

const getters = {
    GET_BANKID: (state) => state.bankID,
    GET_BANKID_ACTIVE: (state) => state.bankID !== null,
    GET_USER_EXISTS_CHECKED: (state) => state.checked,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
